/**
 * HROverview — HR dashboard: candidate stats + recent candidates + job postings.
 * Live data from /v1/hr/stats, /v1/hr/candidates, /v1/hr/job-postings.
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { Avatar, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { usePolledAsync, fetchHRStats, fetchCandidates, fetchJobPostings } from '../../api'
import { attention, danger, ink, statusCandidate, success, surface, warning } from '../../../../theme/tokens'

function scoreColor(score?: number): string {
  if (!score) return ink.muted
  if (score >= 80) return success.DEFAULT
  if (score >= 60) return warning.DEFAULT
  return danger.DEFAULT
}

const STATUS_BADGE: Record<string, { bg: string; color: string }> = {
  pending_review: { bg: statusCandidate.pending_review.bg, color: statusCandidate.pending_review.text },
  shortlisted:    { bg: statusCandidate.shortlisted.bg,    color: statusCandidate.shortlisted.text },
  interviewing:   { bg: statusCandidate.interviewing.bg,   color: statusCandidate.interviewing.text },
  offered:        { bg: statusCandidate.offered.bg,        color: statusCandidate.offered.text },
  rejected:       { bg: statusCandidate.rejected.bg,       color: statusCandidate.rejected.text },
  hired:          { bg: statusCandidate.hired.bg,          color: statusCandidate.hired.text },
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_BADGE[status] ?? { bg: surface.panel, color: ink.secondary }
  return (
    <span className="k-badge" style={{ background: cfg.bg, color: cfg.color }}>
      <span className="k-badge-dot" />{status.replace(/_/g, ' ').toUpperCase()}
    </span>
  )
}

/**
 * The k-metric widget for the HR pages (S-Tier slice 5) — the OpsMetric/
 * SalesMetric/FinanceMetric precedent verbatim: markup-level reuse living
 * WITH the department; DashboardShared's MetricCard stays untouched. The
 * amber tone rides the sensitive/attention family (bg + border only) for
 * quiet-urgency counts (HRProviders' RoATP attention), exactly as slice 4's
 * FinanceMetric carries it — one amber, one meaning.
 */
export function HRMetric({ label, value, note, tone = 'default' }: {
  label: string
  value: string | number
  note?: string
  tone?: 'default' | 'amber'
}) {
  const amber = tone === 'amber'
  return (
    <div className="k-metric"
      style={amber ? { background: attention['banner-bg'], borderColor: attention['banner-border'] } : undefined}>
      <div className="flex items-center justify-between gap-2">
        <span className="k-metric-label uppercase">{label}</span>
      </div>
      <div className="flex flex-col gap-[7px]">
        <div className="flex items-baseline gap-2">
          <span className="k-metric-value">{value}</span>
        </div>
        {note && <div className="k-metric-note">{note}</div>}
      </div>
    </div>
  )
}

export default function HROverview({ onNavigate }: { onNavigate: (page: string) => void }) {
  const { data, loading, error } = usePolledAsync(
    () => Promise.all([fetchHRStats(), fetchCandidates(), fetchJobPostings()]),
    10_000,
    [],
  )

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const [stats, candidates, jobs] = data!
  const recent = candidates.slice(0, 8)
  const activeJobs = jobs.filter(j => j.status === 'active')

  // Emoji decoration retired with the closr tile: the k-metric contract has
  // no icon slot (the Sales P10-1 no-icons precedent) — the label carries it.
  const METRICS = [
    { label: 'Total Candidates', value: stats.total },
    { label: 'Pending Review', value: stats.pendingReview },
    { label: 'Shortlisted', value: stats.shortlisted },
    { label: 'Hired This Month', value: stats.hiredThisMonth },
  ]

  return (
    <div className="page-enter flex flex-col gap-4">
      <div className="grid grid-cols-4 gap-3">
        {METRICS.map(m => <HRMetric key={m.label} label={m.label} value={m.value} />)}
      </div>

      <div className="grid grid-cols-[1fr_280px] gap-4">
        {/* Recent candidates */}
        <div className="k-card">
          <div className="k-card-head">
            <div className="k-card-title">Recent Candidates</div>
            <button className="k-card-link cursor-pointer bg-transparent border-0 p-0 uppercase hover:underline"
              onClick={() => onNavigate('hr.job-postings')}>View pipeline</button>
          </div>
          {recent.length === 0 ? (
            <EmptyState message="No candidates yet" />
          ) : (
            <table className="closr-table hr-table w-full">
              <thead>
                <tr>
                  <th className="text-left">Name</th>
                  <th className="text-left">Role</th>
                  <th className="text-left">AI Score</th>
                  <th className="text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {recent.map(c => (
                  <tr key={c.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <Avatar name={c.fullName} size={26} />
                        <span className="text-13 font-medium text-ink-primary">{c.fullName}</span>
                      </div>
                    </td>
                    <td className="text-12 text-ink-secondary">{c.roleApplied || '—'}</td>
                    <td>
                      <span className="text-12 font-semibold" style={{ color: scoreColor(c.aiScore) }}>
                        {c.aiScore ? `${c.aiScore}` : '—'}
                      </span>
                    </td>
                    <td><StatusBadge status={c.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Active job postings */}
        <div className="k-card">
          <div className="k-card-head">
            <div className="k-card-title">Active Job Postings</div>
            <button className="k-card-link cursor-pointer bg-transparent border-0 p-0 uppercase hover:underline"
              onClick={() => onNavigate('hr.job-postings')}>See All</button>
          </div>
          <div className="divide-y divide-edge-row-faint">
            {activeJobs.length === 0 ? (
              <div className="px-4 py-6 text-center text-12 text-ink-muted">No active postings</div>
            ) : (
              activeJobs.slice(0, 6).map(j => (
                <div key={j.id} className="px-4 py-3">
                  <div className="text-13 font-medium text-ink-primary leading-tight">{j.title}</div>
                  <div className="text-11 text-ink-muted">{j.department || '—'} · {j.applicationsCount} applicants</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
